import { Link } from "react-router-dom";
import { Linkedin } from "lucide-react";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/layout/Breadcrumbs";
import CTASection from "../components/blocks/CTASection";
import SectionHeading from "../components/blocks/SectionHeading";

const FOUNDER_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Rakesh Gopinathan",
  jobTitle: "Founder & Chief Executive Officer",
  description:
    "Rakesh Gopinathan is the Founder & CEO of The Vantage Group™, a strategic business and leadership advisory serving founder-led and growth-stage businesses. His areas of focus include business strategy, cash flow, working capital, contribution margins, customer profitability, pricing, leadership, professionalisation, growth and enterprise value. He is also Founder of Vantage Institute™.",
  image: "/images/rakesh-gopinathan.jpg",
  email: "rakesh@wba.org.in",
  telephone: "+917550192649",
  sameAs: ["https://www.linkedin.com/in/rakeshgopinathan/"],
  worksFor: { "@type": "Organization", name: "The Vantage Group™" },
  founder: [
    { "@type": "Organization", name: "The Vantage Group™" },
    { "@type": "EducationalOrganization", name: "The Vantage Institute™" },
    { "@type": "Organization", name: "Wowrakesh Business Academy®" },
  ],
  knowsAbout: [
    "Business Strategy", "Cash Flow", "Working Capital", "Contribution Margins",
    "Customer Profitability", "Pricing", "Leadership", "Professionalisation",
    "Strategic Growth", "Enterprise Value", "Executive Education",
  ],
};

const ECONOMIC_DRIVERS = [
  "Cash conversion and working capital", "Contribution margins", "Customer profitability",
  "Revenue concentration", "Pricing power", "Revenue quality", "EBITDA quality",
  "Capital allocation", "Operational discipline", "Leadership capability",
  "Founder dependency", "Professionalisation", "Strategic growth", "Enterprise value",
];

const SPECIALISATIONS = [
  {
    n: "01", title: "Business Economics & Value Creation",
    text: "Helping leadership teams understand the economics underneath revenue.",
    chain: "Cash → Margin → Profitability → Capital → Enterprise Value",
  },
  {
    n: "02", title: "Founder-Led Business Transformation",
    text: "Helping founders transition from:",
    chain: "Founder dependency → Management capability → Institutional capability",
  },
  {
    n: "03", title: "Strategic Growth",
    text: "Moving organisations from growth driven primarily by opportunity and intuition toward disciplined, economically sound growth.",
  },
  {
    n: "04", title: "Executive & Leadership Development",
    text: "Developing leaders who can operate with greater strategic clarity, executive presence, accountability and organisational influence.",
  },
  {
    n: "05", title: "Business Professionalisation",
    text: "Helping growing organisations develop the systems, governance, management disciplines and leadership structures required for their next stage.",
  },
];

const FIVE_QUESTIONS = [
  "Where are we creating value?",
  "Where are we destroying value?",
  "Where is our cash going?",
  "What is preventing the organisation from scaling?",
  "What will make the enterprise more valuable?",
];

const INSTITUTE_OUTPUTS = [
  "Executive education programmes", "Leadership frameworks", "Business diagnostics",
  "Research", "Executive development methodologies", "Assessment instruments",
  "Strategic intellectual property", "Executive tools and learning systems",
];

const CYCLE = ["Research", "Insight", "Framework", "Education", "Application", "Advisory", "Evidence", "New Research"];

const Section = ({ title, eyebrow, children, testId }) => (
  <section className="border-t border-platinum py-12" data-testid={testId}>
    <div className="max-w-3xl">
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <h2 className="mt-2 font-display text-2xl font-semibold text-navy sm:text-3xl">{title}</h2>
      <span className="gold-rule mt-4 !w-12" aria-hidden="true" />
    </div>
    <div className="mt-6">{children}</div>
  </section>
);

export default function FounderPage() {
  return (
    <>
      <SEO
        title="Rakesh Gopinathan | Founder & CEO | The Vantage Group™"
        description="Rakesh Gopinathan is Founder & CEO of The Vantage Group™, a strategic business and leadership advisory focused on growth-stage businesses, profitability, cash flow, leadership and enterprise value."
        path="/founder/rakesh-gopinathan"
        jsonLd={FOUNDER_JSONLD}
      />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "About", to: "/about" }, { label: "Rakesh Gopinathan" }]} />

      {/* HERO */}
      <section className="bg-navy" data-testid="founder-hero">
        <div className="container-x py-16 sm:py-24">
          <div className="grid items-start gap-10 lg:grid-cols-[320px_1fr] lg:gap-16">
            <figure>
              <img
                src="/images/rakesh-gopinathan.jpg"
                alt="Portrait of Rakesh Gopinathan, Founder & CEO of The Vantage Group™ and Founder of The Vantage Institute™"
                width="320"
                height="400"
                loading="eager"
                className="w-full max-w-[320px] border border-gold/40 object-cover"
                data-testid="founder-photo"
              />
              <figcaption className="mt-3 font-sub text-[0.7rem] uppercase tracking-[0.14em] text-platinum/70">
                Rakesh Gopinathan — Founder & CEO, The Vantage Group™
              </figcaption>
            </figure>
            <div>
              <p className="eyebrow-light">Founder Authority Profile</p>
              <h1 className="mt-4 font-display text-4xl font-semibold leading-tight text-white sm:text-5xl">
                Rakesh Gopinathan
              </h1>
              <span className="gold-rule mt-6 !w-24" aria-hidden="true" />
              <ul className="mt-6 space-y-1.5 font-sub text-sm font-semibold tracking-wide text-platinum">
                <li>Founder & Chief Executive Officer, The Vantage Group™</li>
                <li>Founder, Vantage Institute™</li>
                <li>Founder & CEO, Wowrakesh Business Academy®</li>
              </ul>
              <p className="mt-4 font-sub text-xs font-semibold uppercase tracking-[0.14em] text-gold">
                Strategic Business Advisor • Executive Education & Leadership Strategist • Business Transformation & Growth Advisor
              </p>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-platinum md:text-lg">
                Rakesh Gopinathan is the Founder & CEO of The Vantage Group™, a strategic business and leadership
                advisory focused on founder-led and growth-stage businesses. His work spans business strategy, cash
                flow, profitability, growth, leadership, professionalisation and enterprise value.
              </p>
              <a
                href="https://www.linkedin.com/in/rakeshgopinathan/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline-light mt-8 !min-h-[44px] !px-5 !py-2 text-xs"
                data-testid="founder-linkedin"
              >
                <Linkedin size={15} aria-hidden="true" />
                Connect on LinkedIn
              </a>
            </div>
          </div>
        </div>
      </section>

      <article className="container-x py-14 sm:py-20" data-testid="founder-profile">
        {/* ABOUT */}
        <Section title="About Rakesh Gopinathan" testId="founder-about">
          <div className="max-w-3xl space-y-5 text-base leading-relaxed text-slate">
            <p>
              Rakesh Gopinathan is the Founder & CEO of The Vantage Group™, a strategic business and leadership
              advisory focused on helping founder-led and growth-stage businesses build stronger, more resilient
              and more valuable enterprises.
            </p>
            <p>
              His work focuses particularly on businesses navigating the transition from founder-driven growth to
              professionally managed, strategically disciplined organisations. Through The Vantage Group™, Rakesh
              works at the intersection of business strategy, financial performance, leadership, organisational
              professionalisation, growth and enterprise value.
            </p>
            <p>
              His advisory perspective is built around a simple principle: a business should not merely become
              bigger. It should become economically stronger, strategically clearer and more valuable.
            </p>
            <p>
              Rather than focusing exclusively on topline growth, Rakesh approaches business growth through the
              lens of cash, profitability, control, resilience and value creation.
            </p>
          </div>
          <div className="mt-8 max-w-3xl">
            <p className="font-sub text-xs font-semibold uppercase tracking-[0.14em] text-gold-deep">
              The underlying drivers of business quality his work addresses
            </p>
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {ECONOMIC_DRIVERS.map((d) => (
                <li key={d} className="flex items-center gap-3 border border-platinum bg-paper px-4 py-2.5 font-sub text-sm font-medium text-navy" data-testid={`driver-${d.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}>
                  <span className="h-px w-5 shrink-0 bg-gold" aria-hidden="true" />
                  {d}
                </li>
              ))}
            </ul>
          </div>
        </Section>

        {/* VANTAGE GROUP */}
        <Section title="The Vantage Group™" eyebrow="Strategic Business & Leadership Advisory" testId="founder-vantage-group">
          <div className="max-w-3xl space-y-5 text-base leading-relaxed text-slate">
            <p>
              Rakesh is the Founder & CEO of The Vantage Group™, a strategic business and leadership advisory
              institution serving founders, promoters, CEOs and leadership teams of growth-stage and mid-market
              businesses.
            </p>
            <p>
              The firm combines strategic thinking, business diagnostics, leadership advisory, financial and
              operating perspectives, and practical execution frameworks.
            </p>
          </div>
          <div className="mt-8 max-w-3xl border-l-2 border-gold bg-paper p-6 sm:p-8">
            <p className="font-sub text-xs font-semibold uppercase tracking-[0.14em] text-gold-deep">
              Five fundamental questions the Group helps businesses answer
            </p>
            <ol className="mt-4 space-y-3">
              {FIVE_QUESTIONS.map((q, i) => (
                <li key={q} className="flex gap-4 font-sub text-sm font-semibold text-navy">
                  <span className="font-display text-gold-deep">{String(i + 1).padStart(2, "0")}</span>
                  {q}
                </li>
              ))}
            </ol>
          </div>
        </Section>

        {/* VANTAGE INSTITUTE */}
        <Section title="Vantage Institute™" eyebrow="Executive Education • Research • Intellectual Capital" testId="founder-vantage-institute">
          <div className="max-w-3xl space-y-5 text-base leading-relaxed text-slate">
            <p>
              Rakesh is also the Founder of Vantage Institute™, the Executive Education, Research & Intellectual
              Capital division of Wowrakesh Business Academy®.
            </p>
            <p>
              This enables ideas developed through research and executive education to be translated into practical
              organisational application.
            </p>
          </div>
          <div className="mt-8 grid gap-10 lg:grid-cols-2">
            <div>
              <p className="font-sub text-xs font-semibold uppercase tracking-[0.14em] text-gold-deep">
                Proprietary intellectual capital developed by the Institute
              </p>
              <ul className="mt-4 space-y-2">
                {INSTITUTE_OUTPUTS.map((o) => (
                  <li key={o} className="flex items-center gap-3 text-sm text-slate">
                    <span className="h-px w-5 shrink-0 bg-gold" aria-hidden="true" />
                    {o}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-sub text-xs font-semibold uppercase tracking-[0.14em] text-gold-deep">
                The Institute's continuous cycle
              </p>
              <ol className="mt-4 space-y-0 border-l-2 border-gold">
                {CYCLE.map((step, i) => (
                  <li key={step} className="relative py-2 pl-6">
                    <span className="absolute -left-[5px] top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-gold" aria-hidden="true" />
                    <p className="font-sub text-sm font-semibold uppercase tracking-[0.1em] text-navy">
                      <span className="mr-2 text-gold-deep">{String(i + 1).padStart(2, "0")}</span>
                      {step}
                    </p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
          <p className="mt-8 max-w-3xl text-sm text-slate">
            Read more about the Institute's philosophy and institutional relationships on the{" "}
            <Link to="/about" className="text-link" data-testid="founder-link-about">About page</Link>.
          </p>
        </Section>

        {/* SPECIALISATION */}
        <Section title="Strategic Specialisation" testId="founder-specialisation">
          <div className="grid gap-5 lg:grid-cols-2">
            {SPECIALISATIONS.map((s) => (
              <div key={s.n} className="card card-hover" data-testid={`specialisation-${s.n}`}>
                <p className="font-display text-2xl font-semibold text-platinum" aria-hidden="true">{s.n}</p>
                <h3 className="mt-2 font-sub text-base font-semibold text-navy">{s.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate">{s.text}</p>
                {s.chain && (
                  <p className="mt-3 border-l-2 border-gold pl-3 font-sub text-xs font-semibold uppercase tracking-[0.08em] text-gold-deep">
                    {s.chain}
                  </p>
                )}
              </div>
            ))}
          </div>
        </Section>

        {/* CORE BELIEF */}
        <section className="border-t border-platinum py-12" data-testid="founder-belief">
          <blockquote className="mx-auto max-w-3xl text-center">
            <p className="font-display text-2xl font-semibold leading-snug text-navy sm:text-3xl">
              "Growth is not the ultimate measure of business success. The real measure is the quality of the
              business you build while you grow."
            </p>
            <footer className="mt-6 font-sub text-xs font-semibold uppercase tracking-[0.14em] text-gold-deep">
              Rakesh Gopinathan
            </footer>
          </blockquote>
          <div className="mx-auto mt-10 max-w-2xl">
            <p className="text-center font-sub text-xs font-semibold uppercase tracking-[0.14em] text-slate">
              A stronger enterprise demonstrates
            </p>
            <ul className="mt-4 grid gap-2 text-center sm:grid-cols-2">
              {[
                "Healthy cash generation", "Strong contribution economics", "Quality revenue",
                "Resilient customers and markets", "Effective leadership", "Professional operating systems",
                "Disciplined capital allocation", "Reduced founder dependency",
              ].map((item) => (
                <li key={item} className="border border-platinum bg-paper px-4 py-2.5 text-sm text-navy">{item}</li>
              ))}
            </ul>
            <p className="mt-4 text-center font-sub text-sm font-semibold uppercase tracking-[0.12em] text-gold-deep">
              = Greater enterprise value
            </p>
          </div>
        </section>

        {/* INSTITUTIONAL PHILOSOPHY */}
        <Section title="Institutional Philosophy" testId="founder-philosophy">
          <div className="max-w-3xl space-y-5 text-base leading-relaxed text-slate">
            <p>
              Rakesh's work is built around the belief that founder-led businesses often reach a point where the
              same behaviours that created the first stage of success can become constraints on the next stage of
              growth.
            </p>
            <p>The challenge is therefore not simply to grow faster. It is to build an organisation capable of:</p>
            <ul className="grid gap-2 sm:grid-cols-2">
              {[
                "Thinking better.", "Deciding faster.", "Executing consistently.", "Generating cash.",
                "Protecting margins.", "Developing leaders.", "Allocating capital intelligently.", "Creating sustainable enterprise value.",
              ].map((item) => (
                <li key={item} className="flex items-center gap-3 font-sub text-sm font-semibold text-navy">
                  <span className="h-px w-5 shrink-0 bg-gold" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </Section>

        {/* IN BRIEF — AEO */}
        <section className="border-t border-platinum py-12" data-testid="founder-in-brief">
          <div className="max-w-3xl">
            <p className="eyebrow">In Brief</p>
            <h2 className="mt-2 font-display text-2xl font-semibold text-navy sm:text-3xl">Who is Rakesh Gopinathan?</h2>
            <span className="gold-rule mt-4 !w-12" aria-hidden="true" />
            <p className="mt-6 border-l-2 border-gold bg-paper p-6 text-base leading-relaxed text-slate">
              Rakesh Gopinathan is the Founder & CEO of The Vantage Group™, a strategic business and leadership
              advisory serving founder-led and growth-stage businesses. His areas of focus include business
              strategy, cash flow, working capital, contribution margins, customer profitability, pricing,
              leadership, professionalisation, growth and enterprise value. He is also Founder of Vantage
              Institute™.
            </p>
          </div>
        </section>
      </article>

      <CTASection
        title="Work with the thinking behind the Institute."
        text="Explore the programmes and frameworks built from this advisory philosophy — or start a conversation."
        primaryLabel="Explore Programmes"
        primaryTo="/programmes"
        secondaryLabel="Talk to Vantage"
        secondaryTo="/contact"
        testId="founder-cta"
      />
    </>
  );
}
